/**
 * The icon set, as inline SVG.
 *
 * Hand-drawn on a 24-grid rather than pulled from a library: a docs tool needs
 * about twenty icons, and a dependency for that is 40 kB and a second visual
 * language to keep in step. `currentColor` throughout, so an icon always matches
 * the text beside it in either theme.
 */
export type IconName =
  /** The brand mark. Not an icon in the usual sense, but it renders like one. */
  | 'logo'
  | 'page'
  | 'pages'
  | 'folder'
  | 'nav'
  | 'people'
  | 'lock'
  | 'search'
  | 'branch'
  | 'plus'
  | 'check'
  | 'alert'
  | 'info'
  | 'chevron-right'
  | 'chevron-down'
  | 'chevron-up'
  | 'chevron-left'
  | 'commit'
  | 'push'
  | 'diff'
  | 'eye'
  | 'code'
  | 'copy'
  | 'blocks'
  | 'callout'
  | 'tabs'
  | 'cards'
  | 'table'
  | 'image'
  | 'divider'
  | 'quote'
  | 'list'
  | 'list-ordered'
  | 'heading'
  | 'text'
  | 'expand'
  | 'trash'
  | 'rename'
  | 'sun'
  | 'moon'
  | 'monitor'
  | 'keyboard'
  | 'close'
  | 'undo'
  | 'history'
  | 'redo'
  | 'link'
  | 'spinner'
  | 'pulse'
  | 'braces'
  | 'chart'
  /** A third party's mark, and the one filled glyph in the set. See FILLED. */
  | 'github'

const P: Record<IconName, string> = {
  /* The brand mark. Three rules joined by two returns: the rules are lines
     of documentation, the returns are it coming back to current. The rules
     ARE the letterform, which is the part that matters — drawing them as
     separate bars was tried and abandoned, because at 16px it stops reading
     as an S and starts reading as `nav` five lines below, which is literally
     three horizontal bars. A company initial must not look like a menu
     button. Canonical copy — BRAND.md 5.2, both favicons and the site
     stylesheet must match it, which a test enforces. */
  logo: 'M19 5H7v7h10v7H5',
  page: 'M6 2.5h7l5 5v14H6zM13 2.5V8h5',
  pages: 'M8 2.5h6l4 4v11H8zM5 6v15h11',
  folder: 'M3 6.5h6l2 2.5h10v11H3z',
  nav: 'M4 6h16M4 12h10M4 18h13',
  /* Two people, not a crowd: this marks who can reach a workspace, and a
     workspace usually has two or three. */
  people:
    'M9.5 11.5a3.4 3.4 0 1 0 0-6.8 3.4 3.4 0 0 0 0 6.8zM3.5 20c0-3.1 2.7-4.9 6-4.9s6 1.8 6 4.9M16.4 5.2a3.4 3.4 0 0 1 0 6.1M20.5 20c0-2.4-1.4-3.9-3.6-4.5',
  /* A padlock: the mark on "confirm it is you". Body, shackle, keyhole. */
  lock: 'M5.5 11h13v9.5h-13zM8.5 11V7.5a3.5 3.5 0 0 1 7 0V11M12 14.5v2.5',
  search: 'M10.5 3a7.5 7.5 0 1 0 0 15 7.5 7.5 0 0 0 0-15zM16 16l5 5',
  branch: 'M7 4v10a4 4 0 0 0 4 4h3M7 4a2 2 0 1 0 0 .01M17 14a2 2 0 1 0 0 .01M7 20a2 2 0 1 0 0 .01',
  plus: 'M12 5v14M5 12h14',
  check: 'M4 12.5l5 5L20 6.5',
  alert: 'M12 3l9.5 17H2.5zM12 10v4.5M12 17.2v.3',
  info: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 11v6M12 7.5v.3',
  'chevron-right': 'M9 5l7 7-7 7',
  'chevron-down': 'M5 9l7 7 7-7',
  'chevron-up': 'M5 15l7-7 7 7',
  'chevron-left': 'M15 5l-7 7 7 7',
  commit: 'M12 8.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7zM12 3v5.5M12 15.5V21',
  push: 'M12 20V5M6 11l6-6 6 6',
  diff: 'M7 4v6M4 7h6M4 17h6M14 7h6M17 14v6M14 17h6',
  eye: 'M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12zM12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z',
  code: 'M9 7l-5 5 5 5M15 7l5 5-5 5',
  copy: 'M8 8h11v12H8zM5 16H4V4h11v1',
  blocks: 'M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z',
  callout: 'M4 5h16v11H9l-5 4z',
  tabs: 'M3 8h6V4h12v16H3zM9 8v12',
  /* Two cards side by side, not a 2x2 grid: the block this represents inserts
     two linked cards, and a 2x2 grid is `blocks` a few lines up — same grid,
     same weight, same icon. Rendered together they were indistinguishable. */
  cards: 'M3 5h8v14H3zM13 5h8v14h-8z',
  table: 'M3 5h18v14H3zM3 10h18M9 10v9M15 10v9',
  image: 'M3 5h18v14H3zM3 16l5-5 4 4 3-3 6 6M15.5 8.5v.3',
  divider: 'M3 12h18M6 7h12M6 17h12',
  /* Two marks, mirrored about the gap between them, the way an opening and a
     closing quotation mark actually relate. The previous drawing translated
     the same shape sideways instead of mirroring it, so both marks opened the
     same way and read as a repeated glyph rather than a pair. */
  quote: 'M8 6H4v6h4l-1 6M16 6h4v6h-4l1 6',
  list: 'M9 6h11M9 12h11M9 18h11M4.5 6v.3M4.5 12v.3M4.5 18v.3',
  /* Numerals, not the illegible zigzag this used to draw for "2" and no mark
     at all for "3" (the row-3 line had nothing to its left, so the icon read
     as a two-item list with a mystery third row). Each digit is drawn small
     but as an actual 1, 2 and 3 — verified by rendering at 16, 24 and 64px,
     see icon-audit*.tmp.mjs run during this change. */
  'list-ordered':
    'M9 6h11M9 12h11M9 18h11M3.4 5l1.1-.9v3.9M3.2 10.8a1.1 1.1 0 1 1 2.2.35c0 1.05-2.2 1.35-2.4 2.65h2.4M3.2 16.4c2.6-.6 2.6 1.5.5 1.7 2.3.1 2.4 2.5-.4 2.2',
  heading: 'M5 5v14M13 5v14M5 12h8M17 9h2v10',
  text: 'M5 6h14M9 6v13M15 6v13M7 19h4M13 19h4',
  expand: 'M6 9l6 6 6-6',
  trash: 'M4 7h16M9 7V4h6v3M6.5 7l1 14h9l1-14M10.5 11v6M13.5 11v6',
  rename: 'M4 20h4L20 8l-4-4L4 16zM14.5 5.5l4 4',
  sun: 'M12 7a5 5 0 1 0 0 10 5 5 0 0 0 0-10zM12 2v2M12 20v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M2 12h2M20 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4',
  moon: 'M20 14.5A8.5 8.5 0 1 1 9.5 4a7 7 0 0 0 10.5 10.5z',
  monitor: 'M3 5h18v11H3zM9 20h6M12 16v4',
  keyboard: 'M2.5 7h19v10h-19zM6 10v.3M9.5 10v.3M13 10v.3M16.5 10v.3M6 13.5h12',
  close: 'M6 6l12 12M18 6L6 18',
  undo: 'M8 10H4V6M4 10a8 8 0 1 1 3 6.2',
  /* A clock with its hand turned back, which is what this panel offers: not
     "time passed" but "go to an earlier point". */
  history: 'M3.5 8.5H8V4M3.6 8.4A9 9 0 1 1 3 12M12 7.5V12l3.5 2.5',
  redo: 'M16 10h4V6M20 10a8 8 0 1 0-3 6.2',
  link: 'M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1',
  spinner: 'M12 3a9 9 0 0 1 9 9',
  /* A heartbeat line: flat, flat, a spike, flat. The rail place for the
     product's own signal, so it earns a drawing rather than borrowing one of
     the others. */
  pulse: 'M2 12h5l2.5-7 4 14 2.5-7H22',
  /* An opening and a closing brace — the variables surface, because {{name}}
     is how a page spells one. Mirrored about the centre, like `quote`. */
  braces:
    'M9.5 4H9a2.5 2.5 0 0 0-2.5 2.5v3c0 1.5-.8 2.1-2.2 2.5 1.4.4 2.2 1 2.2 2.5v3A2.5 2.5 0 0 0 9 20h.5M14.5 4h.5a2.5 2.5 0 0 1 2.5 2.5v3c0 1.5.8 2.1 2.2 2.5-1.4.4-2.2 1-2.2 2.5v3A2.5 2.5 0 0 1 15 20h-.5',
  /* Three bars on a baseline, rising left to right — the analytics place.
     Drawn rather than reusing `pulse`: a heartbeat is the health of the
     documentation and these are the readers of it, and a rail where two
     places share a glyph is a rail where neither is findable. */
  chart: 'M4 20h16M8 20v-6.5M13 20V7M18 20v-4',
  /* GitHub's own mark, drawn on the same 24 grid as everything else and
     filled rather than stroked. It is the one icon here that is not ours, and
     a third party's mark is not a shape to redraw at 1.7px in our hand: an
     outlined octocat is a different logo, and the whole point of putting it on
     "Continue with GitHub" is that somebody recognises it without reading. */
  github:
    'M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12',
}

/**
 * The icons that are filled shapes rather than drawn lines.
 *
 * ONE, and it is a mark we do not own. Everything in this set is a 24-grid
 * line drawing at 1.7px in `currentColor`, and that is what makes twenty-odd
 * glyphs read as one hand. A filled glyph does not, so the exception is a
 * named list with a reason rather than a `fill` somebody set on an icon: the
 * next person who wants a filled icon has to add themselves to this line and
 * say why, in front of a test that asserts what is in it.
 *
 * The reason here is that GitHub's mark belongs to GitHub. It is on the
 * provider door because it is recognised at a glance, and an outlined
 * approximation of it in our own stroke weight would be neither recognised nor
 * theirs. It still paints in `currentColor`, so it matches the label beside it
 * in both themes like every other icon.
 */
const FILLED: ReadonlySet<IconName> = new Set<IconName>(['github'])

interface Props {
  name: IconName
  size?: number
  className?: string
  /** Set when the icon is the only content of a control. */
  label?: string
}

/**
 * Per-icon stroke weight, for the ones that are not line icons.
 *
 * The mark is a letterform, not an icon: drawn at 1.7 like everything else it
 * renders as a hairline S and reads as broken. Everything else stays at 1.7,
 * which is what keeps the set one visual language.
 */
const WEIGHT: Partial<Record<IconName, number>> = { logo: 4 }

export function Icon({ name, size = 16, className, label }: Props) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={WEIGHT[name] ?? 1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`icon${name === 'spinner' ? ' icon--spin' : ''}${className ? ` ${className}` : ''}`}
      aria-hidden={label ? undefined : true}
      role={label ? 'img' : undefined}
      aria-label={label}
      focusable="false"
    >
      {/* The svg keeps the set's own defaults — a `currentColor` stroke over
          no fill — and a filled glyph turns them round on its own path, so
          the exception is visible on the one icon that has it rather than
          hidden in the element every icon shares. */}
      <path d={P[name]} {...(FILLED.has(name) ? { fill: 'currentColor', stroke: 'none' } : null)} />
    </svg>
  )
}
